/**
 * Typed client-side errors.
 *
 * The API always answers a failure with the envelope declared in
 * `@sonauto/schemas` (`zErrorEnvelope`):
 *
 *   { error: { code, message, messageCode?, details?, retryable? }, requestId? }
 *
 * Every screen in the three PWAs reacts to `ApiError.code` — a stable machine
 * string — and NEVER to `message`, which is Vietnamese prose that may change.
 */
import type { ErrorEnvelope } from '@sonauto/schemas';

/**
 * The codes the API can emit. Mirrors `ERROR_STATUS` in
 * `@sonauto/utilities/errors`; kept as a literal union here so a screen that
 * switches on a code gets exhaustiveness checking without importing the
 * server-side error class.
 *
 * `NETWORK_ERROR`, `TIMEOUT` and `OFFLINE` are client-only: they never come
 * from the server, they describe a request that never reached it.
 */
export const API_ERROR_CODES = [
  'VALIDATION_FAILED',
  'MALFORMED_REQUEST',
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'MFA_REQUIRED',
  'FORBIDDEN',
  'PERMISSION_DENIED',
  'TENANT_MISMATCH',
  'BRANCH_MISMATCH',
  'DEVICE_NOT_AUTHORIZED',
  'REASON_REQUIRED',
  'NOT_FOUND',
  'CONFLICT',
  'VERSION_CONFLICT',
  'DUPLICATE_REQUEST',
  'INVALID_STATE',
  'ALREADY_CLAIMED',
  'GONE',
  'QR_EXPIRED',
  'SESSION_CLOSED',
  'PAYLOAD_TOO_LARGE',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
  'NOT_IMPLEMENTED',
  'UPSTREAM_FAILED',
  'UNAVAILABLE',
  // client-only
  'NETWORK_ERROR',
  'TIMEOUT',
  'OFFLINE',
  'ABORTED',
  'UNKNOWN',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

const KNOWN = new Set<string>(API_ERROR_CODES);

export interface ApiErrorInit {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly message: string;
  readonly messageCode?: string | undefined;
  readonly details?: Record<string, unknown> | undefined;
  readonly retryable?: boolean | undefined;
  readonly requestId?: string | undefined;
  readonly cause?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly messageCode: string | null;
  readonly details: Record<string, unknown> | null;
  readonly retryable: boolean;
  readonly requestId: string | null;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause !== undefined ? { cause: init.cause } : undefined);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.messageCode = init.messageCode ?? null;
    this.details = init.details ?? null;
    this.retryable = init.retryable ?? RETRYABLE.has(init.code);
    this.requestId = init.requestId ?? null;
  }

  /** True when the caller should send the user back to the login screen. */
  get isAuthError(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'SESSION_EXPIRED' || this.code === 'MFA_REQUIRED';
  }

  /** True when the caller lacks a permission / scope rather than a session. */
  get isPermissionError(): boolean {
    return (
      this.code === 'PERMISSION_DENIED' ||
      this.code === 'FORBIDDEN' ||
      this.code === 'TENANT_MISMATCH' ||
      this.code === 'BRANCH_MISMATCH' ||
      this.code === 'DEVICE_NOT_AUTHORIZED'
    );
  }

  /** True when the API refused because `x-action-reason` was missing. */
  get needsReason(): boolean {
    return this.code === 'REASON_REQUIRED';
  }

  /** True when the request never left the device. */
  get isConnectivityError(): boolean {
    return this.code === 'OFFLINE' || this.code === 'NETWORK_ERROR' || this.code === 'TIMEOUT';
  }

  /** Field-level validation problems, when the API supplied them. */
  fieldErrors(): Record<string, string> {
    const raw = this.details?.['fields'];
    if (raw === null || typeof raw !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  }
}

const RETRYABLE = new Set<ApiErrorCode>([
  'UNAVAILABLE',
  'UPSTREAM_FAILED',
  'RATE_LIMITED',
  'NETWORK_ERROR',
  'TIMEOUT',
  'OFFLINE',
]);

/** Fallback Vietnamese copy for connectivity problems the server never saw. */
const CLIENT_MESSAGES: Partial<Record<ApiErrorCode, string>> = {
  OFFLINE: 'Thiết bị đang ngoại tuyến. Thao tác sẽ được thử lại khi có mạng.',
  NETWORK_ERROR: 'Không kết nối được máy chủ. Vui lòng kiểm tra đường truyền.',
  TIMEOUT: 'Máy chủ phản hồi quá lâu. Vui lòng thử lại.',
  ABORTED: 'Yêu cầu đã bị hủy.',
  UNKNOWN: 'Lỗi không xác định.',
};

export function clientError(code: ApiErrorCode, status = 0, cause?: unknown): ApiError {
  return new ApiError({
    status,
    code,
    message: CLIENT_MESSAGES[code] ?? 'Lỗi không xác định.',
    cause,
  });
}

/** Narrow an unknown value into the API's error envelope, defensively. */
export function parseErrorEnvelope(body: unknown): ErrorEnvelope | null {
  if (body === null || typeof body !== 'object') return null;
  const outer = body as { error?: unknown; requestId?: unknown };
  if (outer.error === null || typeof outer.error !== 'object') return null;
  const err = outer.error as Record<string, unknown>;
  if (typeof err['code'] !== 'string' || typeof err['message'] !== 'string') return null;
  return {
    error: {
      code: err['code'],
      message: err['message'],
      messageCode: typeof err['messageCode'] === 'string' ? err['messageCode'] : undefined,
      details:
        err['details'] !== null && typeof err['details'] === 'object'
          ? (err['details'] as Record<string, unknown>)
          : undefined,
      retryable: typeof err['retryable'] === 'boolean' ? err['retryable'] : undefined,
    },
    requestId: typeof outer.requestId === 'string' ? outer.requestId : undefined,
  };
}

/**
 * Map an HTTP response + parsed body onto an `ApiError`.
 *
 * An unrecognised `code` is preserved as `UNKNOWN` rather than crashing the UI:
 * a newer API must be able to add codes without breaking older installed PWAs.
 */
export function toApiError(status: number, body: unknown): ApiError {
  const envelope = parseErrorEnvelope(body);
  if (envelope === null) {
    return new ApiError({
      status,
      code: statusToCode(status),
      message: `Máy chủ trả về lỗi ${status}.`,
    });
  }
  const raw = envelope.error.code;
  return new ApiError({
    status,
    code: KNOWN.has(raw) ? (raw as ApiErrorCode) : 'UNKNOWN',
    message: envelope.error.message,
    messageCode: envelope.error.messageCode,
    details: envelope.error.details,
    retryable: envelope.error.retryable,
    requestId: envelope.requestId,
  });
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'PERMISSION_DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 410) return 'GONE';
  if (status === 413) return 'PAYLOAD_TOO_LARGE';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 501) return 'NOT_IMPLEMENTED';
  if (status === 502) return 'UPSTREAM_FAILED';
  if (status === 503) return 'UNAVAILABLE';
  if (status >= 500) return 'INTERNAL_ERROR';
  if (status >= 400) return 'VALIDATION_FAILED';
  return 'UNKNOWN';
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
